import { CentralConfig } from './CentralConfig';
import { ConfigJsonParser } from './ConfigJsonParser';
import { Constants } from '../common/Constants';
import { ConfigurationLoadingError } from '../common/errors/ConfigurationLoadingError';
import { ensureError, isError } from '../common/errors/ErrorUtil';
import { PushConfigStatus, Result } from '../common/InternalTypes';
import { LibInfo } from '../common/LibInfo';
import { LoggerTexts } from '../common/LoggerTexts';
import { ConfigIdentifier, JSONValue, TenantInfo } from '../common/Types';

export class ConfigurationLoader {
	private readonly _configIdentifier: ConfigIdentifier;
	private readonly _tenantInfo: TenantInfo;

	constructor(configIdentifier: ConfigIdentifier, tenantInfo: TenantInfo) {
		this._configIdentifier = configIdentifier;
		this._tenantInfo = tenantInfo;
	}

	async loadConfiguration(): Promise<Result<{ centralConfig: CentralConfig; pushConfigStatus: PushConfigStatus }>> {
		const centralConfigUrl = this._centralConfigurationUrl();
		const result = await this._getConfiguration(centralConfigUrl, 1);
		if (isError(result)) {
			return result;
		}
		return ConfigJsonParser.parseJsonToCentralConfig(this._tenantInfo, result.result);
	}

	private async _getConfiguration(url: URL, retries: number): Promise<Result<JSONValue>> {
		try {
			/*const requestHeaders = new Headers();
			requestHeaders.set('sap-pxapi-version', LibInfo.getGlobalBuildInfo());
			requestHeaders.set('sap-pxapi-unit-id', this._configIdentifier.unitId);
			requestHeaders.set('sap-pxapi-environment', this._configIdentifier.environment);

			const response = await fetch(url, {
				method: 'GET',
				cache: 'no-cache' as RequestCache,
				mode: 'cors' as RequestMode,
				referrerPolicy: 'origin-when-cross-origin',
				credentials: 'omit' as RequestCredentials,
				headers: requestHeaders
			});*/
			const response = await fetch(url);
			const contentType = response.headers.get('content-type');
			if (!contentType || !contentType.includes('application/json')) {
				const error = new ConfigurationLoadingError(
					url,
					undefined,
					undefined,
					LoggerTexts.CFG_LOADER.GET_CENTRAL_CFG_LOADING_INVALID_CONTENT_TYPE
				);
				return { success: false, error: error };
			}
			if (response.ok) {
				return { success: true, result: await response.json() };
			}
			if (retries > 0) {
				return await this._getConfiguration(url, retries - 1);
			}
			return { success: false, error: new ConfigurationLoadingError(url, response.status) };
		} catch (error) {
			const _error = ensureError(error);
			return { success: false, error: new ConfigurationLoadingError(url, undefined, _error) };
		}
	}

	private _centralConfigurationUrl(): URL {
		const centralConfigUrl = new URL(this._configIdentifier.configUrl);
		const searchParams = new URLSearchParams({
			unitId: this._configIdentifier.unitId,
			env: this._configIdentifier.environment,
			tenantId: this._tenantInfo.tenantId.trim().toLowerCase(),
			version: Constants.CURRENT_CFG_VERSION,
			clientVersion: LibInfo.getGlobalBuildInfo()
		});
		centralConfigUrl.search = searchParams.toString();
		return centralConfigUrl;
	}
}
